import { fetchQuery, graphql } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import {
	convertToProjectType,
	type ProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	issueViewSwitchDataFetcherQuery,
	JiraServiceManagementPractice,
} from './__generated__/issueViewSwitchDataFetcherQuery.graphql.ts';

export type IssueViewSwitchData = {
	projectType?: ProjectType;
	userPreference: boolean;
	/** Canonical issue key resolved by Jira. Populated only when `issueKey` is passed in. */
	/** We pass issueKey currently only when FG - wiv-fix-redirection-issue-key is true */
	issueKey?: string;
	isCreatedFromAgenticTemplate?: boolean | null;
	isAgenticWorkItemViewEnabled?: boolean | null;
	/**
	 * The JSM `servicedeskPractices` value (e.g. `INCIDENT_MANAGEMENT`, `SERVICE_REQUEST`).
	 * Populated only when `issueKey` is passed in (the `issueByKey` lookup runs) and the JSM
	 * redirection gate is on — the field is skipped otherwise so non-JSM traffic pays no cost.
	 * Used to redirect JSM issues to their practice-specific route.
	 */
	jsmPractice?: JiraServiceManagementPractice;
};

/**
 * When `issueKey` is passed in, the canonical key resolved by Jira is returned so callers
 * can build redirect URLs that respect project key moves/renames (MWIV-814). Otherwise the
 * `issueByKey` lookup is skipped server-side.
 */
export async function issueViewSwitchDataFetcher(
	projectIdOrKey: string,
	cloudId: string,
	issueKey?: string,
): Promise<IssueViewSwitchData> {
	const safeIssueKey = issueKey ?? '';
	const result = await fetchQuery<issueViewSwitchDataFetcherQuery>(
		getRelayEnvironment(),
		graphql`
			query issueViewSwitchDataFetcherQuery(
				$cloudId: ID!
				$projectIdOrKey: String!
				$issueKey: String!
				$skipIssueByKey: Boolean!
				$includeAgenticWorkItemData: Boolean!
				$includeServicedeskPractice: Boolean!
			) {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
					projectType
					isCreatedFromAgenticTemplate @include(if: $includeAgenticWorkItemData)
					isAgenticWorkItemViewEnabled @include(if: $includeAgenticWorkItemData)
				}
				jira {
					userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
						isIssueViewWivEnabled
					}
					issueByKey(cloudId: $cloudId, key: $issueKey) @skip(if: $skipIssueByKey) {
						key
						servicedeskPractices @include(if: $includeServicedeskPractice)
						project {
							projectType
							isCreatedFromAgenticTemplate @include(if: $includeAgenticWorkItemData)
							isAgenticWorkItemViewEnabled @include(if: $includeAgenticWorkItemData)
						}
					}
				}
			}
		`,
		{
			cloudId,
			projectIdOrKey,
			issueKey: safeIssueKey,
			skipIssueByKey: !safeIssueKey,
			includeAgenticWorkItemData: expVal('work_item_for_quadratic', 'isEnabled', false),
			// Only fetch the practice field when the JSM redirect it powers is enabled, so non-JSM
			// traffic (which never uses it) pays no server-side field-resolution cost.
			includeServicedeskPractice: fg('jsm_aniv_route_redirection'),
		},
		{ fetchPolicy: 'network-only' },
	).toPromise();

	// Prefer the project resolved from the issue because the project key in the URL may be stale
	// after a project move or rename. If the issue cannot be resolved, fall back to the project
	// lookup so Work Item View eligibility can still be evaluated.
	const projectTypeFromQuery =
		issueKey && result?.jira?.issueByKey?.project?.projectType !== undefined
			? result?.jira?.issueByKey?.project?.projectType
			: result?.jira_projectByIdOrKey?.projectType;

	const isCreatedFromAgenticTemplateFromQuery =
		issueKey && result?.jira?.issueByKey?.project?.isCreatedFromAgenticTemplate !== undefined
			? result?.jira?.issueByKey?.project?.isCreatedFromAgenticTemplate
			: result?.jira_projectByIdOrKey?.isCreatedFromAgenticTemplate;

	const isAgenticWorkItemViewEnabledFromQuery =
		issueKey && result?.jira?.issueByKey?.project?.isAgenticWorkItemViewEnabled !== undefined
			? result?.jira?.issueByKey?.project?.isAgenticWorkItemViewEnabled
			: result?.jira_projectByIdOrKey?.isAgenticWorkItemViewEnabled;

	const issueViewSwitchData: IssueViewSwitchData = {
		projectType: projectTypeFromQuery ? convertToProjectType(projectTypeFromQuery) : undefined,
		userPreference: result?.jira?.userPreferences?.isIssueViewWivEnabled ?? true,
		isCreatedFromAgenticTemplate: isCreatedFromAgenticTemplateFromQuery ?? null,
		isAgenticWorkItemViewEnabled: isAgenticWorkItemViewEnabledFromQuery ?? null,
		jsmPractice: result?.jira?.issueByKey?.servicedeskPractices ?? undefined,
	};

	return result?.jira?.issueByKey?.key
		? { ...issueViewSwitchData, issueKey: result.jira.issueByKey.key }
		: issueViewSwitchData;
}
